import { Injectable, OnDestroy } from '@angular/core';
import { FilterCriteria } from '@docma-mm-core/filter/filter-criteria';
import { FilterOperatorEnum } from '@docma-mm-core/filter/filter-operator.enum';
import { LayoutField } from '@docma-mm-core/layout/layout-field';
import { isIdsEquals } from '@docma-mm-core/utils/utils';
import { GridApi } from 'ag-grid-community';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LayoutStateModel } from '../../../mm-filter/layout/layout-state.model';
import { MmLayoutStateService } from '../../../mm-filter/layout/layout-state.service';
import { BooleanFilterOptionEnum, CheckboxFilterOption } from '@mydocma/component-library/src/lib/grid';
import { FilterLinkOperatorEnum } from '@docma-mm-core/filter/filter-link-operator.enum';
import { FieldDataTypeEnum } from '@docma-mm-core/field/field-data-type.enum';


@UntilDestroy()
@Injectable()
export class FilterService implements OnDestroy {

  private layoutState: LayoutStateModel;
  private gridApi: GridApi;

  readonly filterTypeToOperationMap = new Map< string , FilterOperatorEnum>(
    [
      ['contains', FilterOperatorEnum.Like],
      ['equals', FilterOperatorEnum.Equal],
      ['notEqual', FilterOperatorEnum.NotEqual],
      ['lessThan', FilterOperatorEnum.Less],
      ['lessThanOrEqual', FilterOperatorEnum.LessEqual],
      ['greaterThan', FilterOperatorEnum.Greater],
      ['greaterThanOrEqual', FilterOperatorEnum.GreaterEqual],
      ['empty', FilterOperatorEnum.Empty],
      [BooleanFilterOptionEnum.IsTrue, FilterOperatorEnum.IsTrue],
      [BooleanFilterOptionEnum.IsFalse, FilterOperatorEnum.IsFalse]
  ]);

  readonly operationToFilterTypeMap = new Map< FilterOperatorEnum, string>(
    [
      [FilterOperatorEnum.Like, 'contains'],
      [FilterOperatorEnum.Equal, 'equals'],
      [FilterOperatorEnum.NotEqual, 'notEqual'],
      [FilterOperatorEnum.Less, 'lessThan'],
      [FilterOperatorEnum.LessEqual, 'lessThanOrEqual'],
      [FilterOperatorEnum.Greater, 'greaterThan'],
      [FilterOperatorEnum.GreaterEqual, 'greaterThanOrEqual'],
      [FilterOperatorEnum.Empty, 'empty'],
      [FilterOperatorEnum.IsTrue, BooleanFilterOptionEnum.IsTrue],
      [FilterOperatorEnum.IsFalse, BooleanFilterOptionEnum.IsFalse]
  ]);

  constructor(private readonly layoutStateService: MmLayoutStateService) {

    this.layoutStateService.currentLayoutState$
      .pipe(untilDestroyed(this))
      .subscribe(ls => this.onLayoutStateChanged(ls.state));
  }

  ngOnDestroy(): void {
  }

  public attach(gridApi: GridApi) {
    this.gridApi = gridApi;
  }

  private onLayoutStateChanged(newState: LayoutStateModel) {

    const projectChanged = !isIdsEquals(this.layoutState && this.layoutState.project, newState && newState.project);
    const layoutChanged = !isIdsEquals(this.layoutState && this.layoutState.layout, newState && newState.layout);

    this.layoutState = newState;
    if (this.gridApi ) {
        if (projectChanged || layoutChanged) {
          // this.gridApi.setFilterModel(null);
        } else if (!layoutChanged) {
          	this.applyLayoutFilter();
        }
    }

  }

  private applyLayoutFilter() {
    if (this.gridApi) {
      const gridFilter = this.convertToLayoutFilter(this.gridApi.getFilterModel());
      if (this.isFiltersEqual(this.layoutState.filters || [], gridFilter)) {
        return;
      }
      this.gridApi.setFilterModel(this.convertToGridFilter(this.layoutState.filters));
    }
  }

  private isFiltersEqual(filter1: FilterCriteria[], filter2: FilterCriteria[]) {
    if (filter1.length !== filter2.length ) {
      return false;
    }
    if (filter1.length === 0) {
      return true;
    }
    for (const f1 of filter1) {
      const f2  = filter2.find(f => f.Field.Id === f1.Field.Id );
      if (!f2 || f1.LogicalOperator !== f2.LogicalOperator || f1.ComparativeValue !== f2.ComparativeValue) {
        return false;
      }
    }
    return true;
  }

  public applyGridFilter(filterModel: { [key: string]: any }, layoutState?: LayoutStateModel) {
    const layoutFilter = this.convertToLayoutFilter(filterModel, layoutState);

    if (this.isFiltersEqual((this.layoutState?.filters ?? []) || [], layoutFilter )) {
      return;
    }

    this.layoutStateService.setFilter(layoutFilter, layoutState);
  }

  public convertToLayoutFilter(filterModel: { [key: string]: any }, layoutState?: LayoutStateModel) {
    const layoutFilter: FilterCriteria[] = [];
    let id = 0;
    Object.keys(filterModel).forEach(key => {
      const field = (layoutState != null ? layoutState : this.layoutState).layout.Fields.find(f => f.Field.DefaultName === key);
      if (field) {
        const filterData = filterModel[key];


        if (filterData.length > 0) {

          const groupId = id + 1;
          filterData.forEach((filter, index) => {
            const filterCriteria = this.convertToListFilterCriteria(field, filter);
            id++;
            filterCriteria.Id = id;
            filterCriteria.selectedId = filter.id;

            if (layoutFilter.length > 0) {
              filterCriteria.ParentId = layoutFilter[0].Id;
            } else if (layoutFilter.length > 0 && index > 0) {
              filterCriteria.ParentId = groupId;
            }
            if (index > 0) {
              filterCriteria.LinkOperator = FilterLinkOperatorEnum.Or;
            }
            layoutFilter.push(filterCriteria);
          });
        } else {
          const filterCriteria = this.convertToFilterCriteria(field, filterData);
          id++;
          filterCriteria.Id = id;
          if (layoutFilter.length > 0) {
            filterCriteria.ParentId = layoutFilter[0].Id;
          }
          layoutFilter.push(filterCriteria);
        }
      }
    });
    return layoutFilter;
  }

  private convertToFilterCriteria(field: LayoutField, filterModel: any): FilterCriteria {
    const lgOperation = this.filterTypeToOperationMap.get(filterModel.type) || FilterOperatorEnum.Equal;
    if (!lgOperation) {
      console.debug(`Grid filter opertation '${filterModel.type}' not supported`);
      return null;
    }
    const filter = new FilterCriteria();
    filter.Field = field.Field as any;
    filter.LogicalOperator = lgOperation;
    if (filterModel.filterType === 'date') {
      filter.ComparativeValue = filterModel.dateFrom;
    } else {
      filter.ComparativeValue = filterModel.filter;
    }
    return filter;
  }

  private convertToListFilterCriteria(field: LayoutField, filterModel: any): FilterCriteria {
    const lgOperation = FilterOperatorEnum.Equal;
    if (!lgOperation) {
      console.debug(`Grid filter opertation '${filterModel.type}' not supported`);
      return null;
    }
    const filter = new FilterCriteria();
    filter.ListFilter = true;
    filter.Field = field.Field as any;
    filter.ComparativeValue = filterModel.value;
    filter.text = filterModel.text;
    filter.LogicalOperator = lgOperation;

    if (field.Field.DataType === FieldDataTypeEnum.Boolean) {
        filter.LogicalOperator = filterModel.value ?  FilterOperatorEnum.IsTrue : FilterOperatorEnum.IsFalse;
    }
    return filter;
  }


  public convertToGridFilter(filters: FilterCriteria[]) {
    if (!filters || filters.length === 0 ) {
      return null;
    }
    const gridFilter =  {};
    filters.forEach(filter => {
      const type = this.operationToFilterTypeMap.get(filter.LogicalOperator);
      const fieldName = (filter.Field as any)?.DefaultName;

      if (filter.ListFilter)  {
        const listFilter: CheckboxFilterOption = {
          value: filter.ComparativeValue,
          text: filter.text,
          selected: true,
          id: filter.selectedId
        };
        gridFilter[fieldName] = gridFilter[fieldName] ? [...gridFilter[fieldName], listFilter]
                                                      : [listFilter];
      }
      else if (type) {
        gridFilter[fieldName] = {type , filter: filter.ComparativeValue};
      }
    });
    return gridFilter;
  }

  // private async moveNewGridFilterToEnd(arr1: FilterCriteria[], arr2: FilterCriteria[]) {
  //   let resultArray = [...arr1];

  //   const newElement = arr1.filter(obj1 => !arr2.some(obj2 => obj2.Field.Id == obj1.Field.Id))[0];
  //   if(newElement) {
  //     const newElementIndex = arr1.findIndex((e) => e.Field.Id == newElement.Field.Id);
  //     console.log(newElementIndex);
  //     resultArray.splice(newElementIndex, 1);
  //     resultArray.push(newElement);
  //   }
  //   return resultArray;
  // }

}
